const DEFAULT_RETRY_DELAYS_MS = [500, 2000];
const DEFAULT_SELF_HEAL_DELAYS_MS = [30_000, 60_000, 120_000];
class HttpError extends Error {
    status;
    constructor(url, status) {
        super(`${url} ${status}`);
        this.status = status;
    }
}
// Fetch rejects network failures with TypeError (per spec, browsers and
// undici alike); anything else non-HTTP (JSON parse errors on a 200,
// aborts) is not transient — fail fast and let fallback/self-heal handle it.
const isTransient = (error) => error instanceof HttpError ? error.status >= 500 : error instanceof TypeError;
const wait = (ms) => new Promise(resolve => setTimeout(resolve, ms));
export const ResilientBackendFetch = (cdnUrl, options) => (tolgee, tools) => {
    const retryDelaysMs = options?.retryDelaysMs ?? DEFAULT_RETRY_DELAYS_MS;
    const selfHealDelaysMs = options?.selfHealDelaysMs ?? DEFAULT_SELF_HEAL_DELAYS_MS;
    const selfHealState = new Map();
    const inFlightSelfHeals = new Set();
    const recordKey = (language, namespace) => `${language}:${namespace ?? ""}`;
    const scheduleSelfHeal = (language, namespace) => {
        if (!options?.selfHeal) {
            return;
        }
        const key = recordKey(language, namespace);
        const state = selfHealState.get(key) ?? {
            attempts: 0,
            timer: undefined,
        };
        if (state.timer !== undefined ||
            state.attempts >= selfHealDelaysMs.length) {
            return;
        }
        const delayMs = selfHealDelaysMs[state.attempts] ?? 0;
        state.attempts += 1;
        state.timer = setTimeout(() => {
            state.timer = undefined;
            inFlightSelfHeals.add(key);
            tolgee
                .loadRecord({ language, namespace })
                .catch(() => undefined)
                .finally(() => inFlightSelfHeals.delete(key));
        }, delayMs);
        selfHealState.set(key, state);
    };
    const clearSelfHeal = (language, namespace) => {
        const key = recordKey(language, namespace);
        const state = selfHealState.get(key);
        if (state?.timer !== undefined) {
            clearTimeout(state.timer);
        }
        selfHealState.delete(key);
    };
    tools.addBackend({
        async getRecord({ namespace, language, fetch: tolgeeFetch }) {
            const path = `${cdnUrl}/${namespace}/${language}.json`;
            for (let attempt = 0;; attempt++) {
                try {
                    const response = await tolgeeFetch(path, {
                        headers: { Accept: "application/json" },
                    });
                    if (!response.ok) {
                        throw new HttpError(response.url, response.status);
                    }
                    const record = await response.json();
                    clearSelfHeal(language, namespace);
                    return record;
                }
                catch (error) {
                    const canRetry = attempt < retryDelaysMs.length && isTransient(error);
                    if (!canRetry) {
                        console.warn(`Tolgee: Failed to fetch translations from ${path}`, error);
                        try {
                            options?.onFetchFailed?.({
                                language,
                                namespace,
                                url: path,
                                error,
                                phase: inFlightSelfHeals.has(recordKey(language, namespace))
                                    ? "selfHeal"
                                    : "load",
                            });
                        }
                        catch (callbackError) {
                            // A broken telemetry callback must never break record
                            // loading (a rejected getRecord fails changeLanguage).
                            console.warn("Tolgee: onFetchFailed callback threw", callbackError);
                        }
                        scheduleSelfHeal(language, namespace);
                        return undefined;
                    }
                    await wait(retryDelaysMs[attempt] ?? 0);
                }
            }
        },
    });
    return tolgee;
};
//# sourceMappingURL=resilientBackendFetch.js.map